import React from 'react';

import { TOAST_ANIM } from '../../theme/tokens';

/**
 * Write `value` to the clipboard. Prefers the async Clipboard API and falls back
 * to a throwaway off-screen `<textarea>` + `document.execCommand('copy')`: the
 * modern API is unavailable outside a secure context (a LAN-IP dev box, some
 * embedded webviews) and can be denied by permissions. Resolves to whether the
 * copy actually landed. Never throws.
 */
export const copyToClipboard = async (value: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Denied or non-secure context: fall through to the legacy path.
  }
  let area: HTMLTextAreaElement | null = null;
  try {
    area = document.createElement('textarea');
    area.value = value;
    area.setAttribute('readonly', '');
    // Off-screen rather than display:none, a hidden element cannot be selected.
    area.style.position = 'fixed';
    area.style.top = '0';
    area.style.left = '0';
    area.style.opacity = '0';
    area.style.pointerEvents = 'none';
    document.body.appendChild(area);
    area.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    area?.remove();
  }
};

export type UseClipboardOptions = {
  /** How long `copied` stays true after a successful copy. Defaults to the toast hold. */
  resetMs?: number;
};

export type UseClipboard = {
  /** True for `resetMs` after a copy that reached the clipboard. */
  copied: boolean;
  /** Copy `value`; resolves to whether it landed (and flips `copied` when it did). */
  copy: (value: string) => Promise<boolean>;
};

/**
 * Clipboard state for a copy control: `copy()` writes the value and, on success,
 * holds `copied` true for `resetMs` (the same "confirmation lingers" timing as the
 * toast, TOAST_ANIM.HOLD_MS) before dropping it. A repeat copy restarts the hold;
 * a pending hold is cleared on unmount.
 */
export const useClipboard = ({ resetMs = TOAST_ANIM.HOLD_MS }: UseClipboardOptions = {}): UseClipboard => {
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef<number | undefined>(undefined);
  const mounted = React.useRef(false);

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      window.clearTimeout(timer.current);
    };
  }, []);

  const copy = React.useCallback(
    async (value: string): Promise<boolean> => {
      const ok = await copyToClipboard(value);
      // The write is async; the control may have unmounted while it was pending.
      if (!ok || !mounted.current) return ok;
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), resetMs);
      return ok;
    },
    [resetMs],
  );

  return { copied, copy };
};

export default useClipboard;
